export type SemanticWebEndpointRecord = Record<string, unknown> & {
  url: string;
};

const WEB_ENDPOINT_SEMANTICS = new Set(["web", "http", "https"]);
const PRIMARY_ENDPOINT_PURPOSES = new Set(["tested_target", "runner_entry"]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readTrimmedString(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed || null;
}

function hasExplicitHttpHref(endpoint: Record<string, unknown>): endpoint is SemanticWebEndpointRecord {
  const href = readTrimmedString(endpoint.url);
  return Boolean(href && /^https?:\/\//i.test(href));
}

function hasWebEndpointSemantics(endpoint: Record<string, unknown>): boolean {
  return [endpoint.kind, endpoint.protocol, endpoint.service_protocol]
    .map(readTrimmedString)
    .some((value) => Boolean(value && WEB_ENDPOINT_SEMANTICS.has(value)));
}

function readEndpointPurpose(endpoint: Record<string, unknown>): string | null {
  const directPurpose = readTrimmedString(endpoint.purpose);
  if (directPurpose) return directPurpose;
  const metadataJson = endpoint.metadata_json;
  if (!isRecord(metadataJson)) return null;
  return readTrimmedString(metadataJson.purpose);
}

export function findSemanticWebEndpoint(record: { access_endpoints?: unknown } | null | undefined): SemanticWebEndpointRecord | null {
  const endpoints = record?.access_endpoints;
  if (!Array.isArray(endpoints)) return null;

  const webEndpoints = endpoints.filter((endpoint): endpoint is SemanticWebEndpointRecord => {
    if (!isRecord(endpoint)) return false;
    return hasExplicitHttpHref(endpoint) && hasWebEndpointSemantics(endpoint);
  });

  const primaryEndpoints = webEndpoints.filter((endpoint) => {
    const purpose = readEndpointPurpose(endpoint);
    return Boolean(purpose && PRIMARY_ENDPOINT_PURPOSES.has(purpose));
  });

  if (primaryEndpoints.length === 1) return primaryEndpoints[0] ?? null;
  if (primaryEndpoints.length > 1) return null;
  if (webEndpoints.length === 1) return webEndpoints[0] ?? null;
  return null;
}

export function readSemanticWebEndpointHref(record: { access_endpoints?: unknown } | null | undefined): string | null {
  return findSemanticWebEndpoint(record)?.url.trim() ?? null;
}
